import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";
import ChunkLoadErrorBoundary from "@/components/ChunkLoadErrorBoundary";
import { initMatomo } from "@/utils/matomo";
import { installChunkLoadRecovery } from "@/utils/chunkLoadRecovery";

if (typeof window !== "undefined") {
  installChunkLoadRecovery();
  let matomoScheduled = false;
  const scheduleMatomoInit = () => {
    if (matomoScheduled) return;
    matomoScheduled = true;
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(() => initMatomo(), { timeout: 2000 });
      return;
    }
    window.setTimeout(() => initMatomo(), 1200);
  };

  const onFirstInteraction = () => scheduleMatomoInit();
  const interactionEvents = ["pointerdown", "keydown", "touchstart"];
  interactionEvents.forEach((eventName) => {
    window.addEventListener(eventName, onFirstInteraction, { once: true, passive: true });
  });

  if (document.visibilityState === "visible") {
    // After load keeps matomo.js off the critical path without losing visitors who leave within seconds.
    if (document.readyState === "complete") scheduleMatomoInit();
    else window.addEventListener("load", scheduleMatomoInit, { once: true });
  } else {
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      document.removeEventListener("visibilitychange", onVisible);
      window.setTimeout(scheduleMatomoInit, 1200);
    };
    document.addEventListener("visibilitychange", onVisible);
  }
}

/** Always mount the SPA, including over server SEO HTML for `/models`. */
export function mountApp(rootElement = document.getElementById("root")) {
  if (!rootElement) return null;
  const root = ReactDOM.createRoot(rootElement);
  // Dev keeps React's error overlay. Production reloads when a deploy deleted a chunk this tab still requests.
  const app = process.env.NODE_ENV === "production"
    ? <ChunkLoadErrorBoundary><App /></ChunkLoadErrorBoundary>
    : <App />;
  root.render(
    <React.StrictMode>
      {app}
    </React.StrictMode>,
  );
  return root;
}

if (process.env.NODE_ENV !== "test") {
  mountApp();
}
